/**
 * CandleScript - Executor
 * Main interface for running indicator code in the sandboxed Web Worker.
 * Handles worker lifecycle, message passing, timeouts, and caching.
 */

import { validateCode } from './static-analyzer.js';

// Worker URL will be resolved by webpack worker-loader
// We use inline worker creation for broader compatibility
const WORKER_BLOB_URL = null; // Set lazily

const EXECUTION_TIMEOUT = 5000; // 5 seconds
const MAX_WORKERS = 2;

/**
 * Computation cache for repeated calculations
 */
class ComputationCache {
    constructor(maxSize = 50) {
        this._cache = new Map();
        this._maxSize = maxSize;
    }

    _makeKey(code, optionsHash, input) {
        // Exact content key: equal lengths do not imply equal rules or prices.
        return JSON.stringify([code, optionsHash, input], (_, value) => ArrayBuffer.isView(value) ? Array.from(value) : value);
    }

    get(code, options, input) {
        const key = this._makeKey(code, JSON.stringify(options), input);
        return this._cache.get(key) || null;
    }

    set(code, options, input, result) {
        const key = this._makeKey(code, JSON.stringify(options), input);
        if (this._cache.size >= this._maxSize) {
            // Evict oldest entry
            const firstKey = this._cache.keys().next().value;
            this._cache.delete(firstKey);
        }
        this._cache.set(key, result);
    }

    clear() {
        this._cache.clear();
    }
}

/**
 * CandleScript Executor
 */
export class CandleScriptExecutor {
    constructor() {
        this._worker = null;
        this._ready = false;
        this._pendingCallbacks = new Map();
        this._messageId = 0;
        this._cache = new ComputationCache();
        this._workerBlobUrl = null;
    }

    /**
     * Initialize the Web Worker
     */
    async init() {
        if (this._worker) return;

        return new Promise((resolve, reject) => {
            try {
                // Create worker from the sandbox-worker.js file
                // Using Blob URL for inline worker creation
                this._worker = this._createWorker();

                this._worker.onmessage = (e) => {
                    this._handleMessage(e.data);
                };

                this._worker.onerror = (err) => {
                    console.error('[CandleScript] Worker error:', err);
                    // Reject all pending callbacks
                    for (const [id, cb] of this._pendingCallbacks) {
                        cb.reject(new Error('Worker error: ' + (err.message || 'Unknown error')));
                    }
                    this._pendingCallbacks.clear();
                };

                // Wait for ready signal
                const readyHandler = (e) => {
                    if (e.data && e.data.type === 'ready') {
                        this._ready = true;
                        resolve();
                    }
                };

                // Temporarily add ready listener
                const origHandler = this._worker.onmessage;
                this._worker.onmessage = (e) => {
                    if (e.data && e.data.type === 'ready') {
                        this._ready = true;
                        this._worker.onmessage = origHandler;
                        resolve();
                    } else {
                        origHandler(e);
                    }
                };

                // Timeout for worker initialization
                setTimeout(() => {
                    if (!this._ready) {
                        reject(new Error('Worker initialization timeout'));
                    }
                }, 3000);
            } catch (err) {
                reject(err);
            }
        });
    }

    /**
     * Create the worker using inline Blob URL
     */
    _createWorker() {
        // Use worker-loader (configured in webpack for sandbox-worker.js)
        try {
            const SandboxWorker = require('./sandbox-worker.js');
            return new SandboxWorker();
        } catch (e) {
            console.warn('[CandleScript] worker-loader not available, using Blob fallback');
        }

        // Fallback: create worker from Blob URL
        // This embeds the worker source inline for environments without worker-loader
        const workerSource = `
            // Minimal sandbox worker fallback
            Object.freeze(Object.prototype);
            Object.freeze(Array.prototype);
            Object.freeze(Function.prototype);

            ${this._getTASource()}

            const API = Object.freeze({
                sma, ema, rsi, atr, highest, lowest, sum, stddev,
                crossover, crossunder, bbands, macd, adx, VisualTrading
            });

            ${this._getDrawingContextSource()}

            self.onmessage = function(e) {
                const { id, type, code, input, options } = e.data;
                if (type === 'execute') {
                    try {
                        const typedInput = {};
                        for (const key of Object.keys(input)) {
                            typedInput[key] = new Float64Array(input[key]);
                        }
                        const drawCtx = new DrawingContext();
                        const scopeVars = Object.assign({
                            Math, NaN, Infinity, isNaN, isFinite, parseFloat, parseInt,
                            Float64Array, Uint8Array, Array, Object, Number, String, Boolean,
                            console: Object.freeze({ log(){}, warn(){}, error(){} }),
                            plot: drawCtx
                        }, API);
                        const keys = Object.keys(scopeVars);
                        const vals = keys.map(k => scopeVars[k]);
                        const wrapped = '"use strict";' + code + ';if(typeof indicator!=="function")throw new Error("No indicator function");return indicator(input,options);';
                        const fn = new Function(...keys, 'input', 'options', wrapped);
                        const start = Date.now();
                        const result = fn(...vals, typedInput, options || {});
                        const elapsed = Date.now() - start;
                        // Serialize typed arrays
                        function serialize(r) {
                            if (!r) return r;
                            if (r instanceof Float64Array || r instanceof Uint8Array) return Array.from(r);
                            if (Array.isArray(r)) return r.map(item => {
                                if (item && typeof item === 'object') {
                                    const s = {};
                                    for (const k of Object.keys(item)) s[k] = serialize(item[k]);
                                    return s;
                                }
                                return item;
                            });
                            if (typeof r === 'object') {
                                const s = {};
                                for (const k of Object.keys(r)) {
                                    const v = r[k];
                                    s[k] = (v instanceof Float64Array || v instanceof Uint8Array) ? Array.from(v) : serialize(v);
                                }
                                return s;
                            }
                            return r;
                        }
                        // Attach drawings if any were produced
                        let finalResult = result;
                        if (drawCtx.drawings.length > 0) {
                            finalResult = { __data: serialize(result), __drawings: drawCtx.drawings };
                        } else {
                            finalResult = serialize(result);
                        }
                        self.postMessage({ id, type: 'result', result: finalResult, elapsed });
                    } catch(err) {
                        self.postMessage({ id, type: 'error', error: { message: err.message, name: err.name } });
                    }
                } else if (type === 'ping') {
                    self.postMessage({ id, type: 'pong' });
                }
            };
            self.postMessage({ type: 'ready' });
        `;
        const blob = new Blob([workerSource], { type: 'application/javascript' });
        this._workerBlobUrl = URL.createObjectURL(blob);
        return new Worker(this._workerBlobUrl);
    }

    /**
     * Get TA function source for Blob worker fallback
     */
    _getTASource() {
        // Inline minimal TA functions for the Blob fallback
        return `
function sma(arr, length) {
    const result = new Float64Array(arr.length).fill(NaN);
    if (!Number.isInteger(length) || length <= 0) return result;
    let sum = 0, missing = 0;
    for (let i = 0; i < arr.length; i++) {
        if (Number.isFinite(arr[i])) sum += arr[i]; else missing++;
        if (i >= length) {
            if (Number.isFinite(arr[i-length])) sum -= arr[i-length]; else missing--;
        }
        if (i >= length-1 && missing === 0) result[i] = sum / length;
    }
    return result;
}
function ema(a,l){const r=new Float64Array(a.length).fill(NaN);if(!Number.isInteger(l)||l<=0)return r;const k=2/(l+1);let s=0,c=0,p=NaN;for(let i=0;i<a.length;i++){if(!Number.isFinite(a[i])){s=0;c=0;p=NaN;continue}if(c<l){s+=a[i];c++;if(c===l)p=r[i]=s/l}else p=r[i]=a[i]*k+p*(1-k)}return r}
function rsi(a,l){const n=a.length,r=new Float64Array(n);if(l<=0||n<l+1)return r;let ag=0,al=0;for(let i=1;i<=l;i++){const c=a[i]-a[i-1];if(c>0)ag+=c;else al+=Math.abs(c);r[i]=NaN}r[0]=NaN;ag/=l;al/=l;r[l]=al===0?100:100-(100/(1+ag/al));for(let i=l+1;i<n;i++){const c=a[i]-a[i-1];ag=(ag*(l-1)+(c>0?c:0))/l;al=(al*(l-1)+(c<0?Math.abs(c):0))/l;r[i]=al===0?100:100-(100/(1+ag/al))}return r}
function atr(h,l,c,len){const n=h.length,tr=new Float64Array(n),r=new Float64Array(n);tr[0]=h[0]-l[0];for(let i=1;i<n;i++)tr[i]=Math.max(h[i]-l[i],Math.abs(h[i]-c[i-1]),Math.abs(l[i]-c[i-1]));if(len<=0||len>n)return r;let s=0;for(let i=0;i<len;i++){s+=tr[i];r[i]=NaN}r[len-1]=s/len;for(let i=len;i<n;i++)r[i]=(r[i-1]*(len-1)+tr[i])/len;return r}
function highest(a,l){const n=a.length,r=new Float64Array(n);for(let i=0;i<n;i++){if(i<l-1){r[i]=NaN;continue}let m=-Infinity;for(let j=i-l+1;j<=i;j++)if(a[j]>m)m=a[j];r[i]=m}return r}
function lowest(a,l){const n=a.length,r=new Float64Array(n);for(let i=0;i<n;i++){if(i<l-1){r[i]=NaN;continue}let m=Infinity;for(let j=i-l+1;j<=i;j++)if(a[j]<m)m=a[j];r[i]=m}return r}
function sum(a,l){const n=a.length,r=new Float64Array(n);let s=0;for(let i=0;i<n;i++){s+=a[i];if(i>=l)s-=a[i-l];r[i]=i>=l-1?s:NaN}return r}
function stddev(a,l){const n=a.length,r=new Float64Array(n);if(l<=1)return r;for(let i=0;i<n;i++){if(i<l-1){r[i]=NaN;continue}let s=0,s2=0;for(let j=i-l+1;j<=i;j++){s+=a[j];s2+=a[j]*a[j]}const m=s/l;r[i]=Math.sqrt(Math.max(0,s2/l-m*m))}return r}
function crossover(a,b){const n=a.length,r=new Uint8Array(n);const sc=typeof b==='number';for(let i=1;i<n;i++){const bp=sc?b:b[i-1],bc=sc?b:b[i];if(a[i-1]<=bp&&a[i]>bc)r[i]=1}return r}
function crossunder(a,b){const n=a.length,r=new Uint8Array(n);const sc=typeof b==='number';for(let i=1;i<n;i++){const bp=sc?b:b[i-1],bc=sc?b:b[i];if(a[i-1]>=bp&&a[i]<bc)r[i]=1}return r}
function bbands(a,l,mult){const m=sma(a,l),sd=stddev(a,l),n=a.length,u=new Float64Array(n),lo=new Float64Array(n);const mu=mult||2;for(let i=0;i<n;i++){u[i]=m[i]+sd[i]*mu;lo[i]=m[i]-sd[i]*mu}return{upper:u,middle:m,lower:lo}}
function adx(high,low,close,length){const out=new Float64Array(close.length).fill(NaN);let tr=0,plus=0,minus=0,dxSum=0,previous=NaN;for(let i=1;i<close.length;i++){const up=high[i]-high[i-1],down=low[i-1]-low[i],t=Math.max(high[i]-low[i],Math.abs(high[i]-close[i-1]),Math.abs(low[i]-close[i-1])),p=up>down&&up>0?up:0,m=down>up&&down>0?down:0;if(i<=length){tr+=t;plus+=p;minus+=m;}else{tr=tr-tr/length+t;plus=plus-plus/length+p;minus=minus-minus/length+m;}if(i>=length){const dx=plus+minus?100*Math.abs(plus-minus)/(plus+minus):0;if(i<2*length)dxSum+=dx;if(i===2*length-1)previous=dxSum/length;else if(i>=2*length)previous=(previous*(length-1)+dx)/length;out[i]=previous;}}return out;}
function macd(a,fast,slow,sig){const f=ema(a,fast||12),s=ema(a,slow||26),n=a.length,ml=new Float64Array(n);for(let i=0;i<n;i++)ml[i]=f[i]-s[i];const sl=ema(ml,sig||9),h=new Float64Array(n);for(let i=0;i<n;i++)h[i]=ml[i]-sl[i];return{macd:ml,signal:sl,histogram:h}}

function VisualTrading(input, options) {
    const { open, high, low, close } = input;
    const n = close.length;
    const smaLen = (options && options.smaLength) || 50;
    const rrr = (options && options.riskRewardRatio) || 3;

    const buy = new Float64Array(n);
    const sell = new Float64Array(n);
    const stopLoss = new Float64Array(n);
    const takeProfit = new Float64Array(n);

    const maArr = sma(close, smaLen);

    let prevSmaColor = '';
    let trendChanged = false;
    let starFormed = false;
    let blackCandleFormed = false;
    let entryDirection = '';
    let entryPoint = 0;
    let entrySL = 0;
    let entryTarget = 0;
    let prevBlackCandle = false;
    let prevOpen = 0, prevHigh = 0, prevLow = 0, prevClose = 0;

    for (let i = 0; i < n; i++) {
        const o = open[i], h = high[i], l = low[i], c = close[i];
        const candleSize = Math.abs(h - l);
        const bodySize = Math.abs(o - c);
        const isBlackCandle = candleSize > 0 && bodySize <= candleSize / 2;
        const curSmaColor = (!isNaN(maArr[i]) && i > 0 && maArr[i] > maArr[i - 1]) ? 'GREEN' : 'RED';

        if (i > 0 && curSmaColor !== prevSmaColor && prevSmaColor !== '') {
            trendChanged = true;
            starFormed = false;
            blackCandleFormed = false;
            entryDirection = '';
            entryPoint = 0;
            entrySL = 0;
            entryTarget = 0;
        } else if (i > 0 && curSmaColor === prevSmaColor) {
            trendChanged = false;
        }

        if (prevBlackCandle && trendChanged) {
            starFormed = true;
            blackCandleFormed = true;
            if (curSmaColor === 'GREEN') {
                entryDirection = 'BUY';
                entryPoint = prevHigh;
                entrySL = prevLow;
                entryTarget = entryPoint + (entryPoint - entrySL) * rrr;
            } else {
                entryDirection = 'SELL';
                entryPoint = prevLow;
                entrySL = prevHigh;
                entryTarget = entryPoint - (entrySL - entryPoint) * rrr;
            }
        } else {
            starFormed = false;
        }

        if ((starFormed || trendChanged) && isBlackCandle) {
            blackCandleFormed = true;
            if (curSmaColor === 'GREEN') {
                entryDirection = 'BUY';
                if (entryPoint === 0) entryPoint = h;
                if (entrySL === 0) entrySL = l;
                if (l < entrySL) entrySL = l;
                if (h > entryPoint) entryPoint = h;
                entryTarget = entryPoint + (entryPoint - entrySL) * rrr;
            } else {
                entryDirection = 'SELL';
                if (entryPoint === 0) entryPoint = l;
                if (entrySL === 0) entrySL = h;
                if (h > entrySL) entrySL = h;
                if (l < entryPoint) entryPoint = l;
                entryTarget = entryPoint - (entrySL - entryPoint) * rrr;
            }
        }

        if ((starFormed || trendChanged) && blackCandleFormed && !isBlackCandle) {
            trendChanged = false;
            if (entryDirection === 'BUY' && entryPoint > 0 && entrySL > 0) {
                buy[i] = 1;
                stopLoss[i] = entrySL;
                takeProfit[i] = entryTarget;
            } else if (entryDirection === 'SELL' && entryPoint > 0 && entrySL > 0) {
                sell[i] = 1;
                stopLoss[i] = entrySL;
                takeProfit[i] = entryTarget;
            }
            blackCandleFormed = false;
            starFormed = false;
            entryDirection = '';
            entryPoint = 0;
            entrySL = 0;
            entryTarget = 0;
        }

        if (i > 0 && prevBlackCandle) {
            const p_o = prevOpen, p_h = prevHigh, p_l = prevLow, p_c = prevClose;
            let wick1 = p_o > p_c ? p_h - p_o : p_h - p_c;
            let wick2 = p_o > p_c ? p_c - p_l : p_o - p_l;
            const wickSize = Math.max(wick1, wick2);
            if (c >= p_h && buy[i] === 0) {
                buy[i] = 1;
                stopLoss[i] = p_l;
                takeProfit[i] = p_h + wickSize * rrr;
            } else if (c <= p_l && sell[i] === 0) {
                sell[i] = 1;
                stopLoss[i] = p_h;
                takeProfit[i] = p_l - wickSize * rrr;
            }
        }

        if (stopLoss[i] === 0 && i > 0) stopLoss[i] = stopLoss[i - 1];
        if (takeProfit[i] === 0 && i > 0) takeProfit[i] = takeProfit[i - 1];

        prevSmaColor = curSmaColor;
        prevBlackCandle = isBlackCandle;
        prevOpen = o; prevHigh = h; prevLow = l; prevClose = c;
    }
    return { buy, sell, stopLoss, takeProfit };
}
        `;
    }

    /**
     * Get DrawingContext class source for Blob worker fallback
     */
    _getDrawingContextSource() {
        return `
class DrawingContext {
    constructor() { this.drawings = []; }
    line(values, opts = {}) {
        this.drawings.push({
            type: 'line',
            data: values instanceof Float64Array ? Array.from(values) : values,
            color: opts.color || '#2962ff',
            width: opts.width || 1.5,
            style: opts.style || 'solid',
            title: opts.title || '',
            offset: opts.offset || 0,
        });
    }
    hline(price, opts = {}) {
        this.drawings.push({
            type: 'hline',
            price: price,
            color: opts.color || '#787b86',
            width: opts.width || 1,
            style: opts.style || 'dashed',
            title: opts.title || '',
        });
    }
    shape(series, opts = {}) {
        this.drawings.push({
            type: 'shape',
            data: series instanceof Float64Array ? Array.from(series) : series,
            shape: opts.shape || 'circle',
            color: opts.color || '#2962ff',
            size: opts.size || 1,
            location: opts.location || 'absolute',
            title: opts.title || '',
            text: opts.text || '',
        });
    }
    text(barIndex, price, text, opts = {}) {
        this.drawings.push({
            type: 'text',
            barIndex: barIndex,
            price: price,
            text: text,
            color: opts.color || '#131722',
            size: opts.size || 12,
            location: opts.location || 'absolute',
            offset: opts.offset || 0,
        });
    }
    textSeries(series, opts = {}) {
        this.drawings.push({
            type: 'text_series',
            data: series,
            color: opts.color || '#131722',
            size: opts.size || 11,
            location: opts.location || 'absolute',
        });
    }
    candle(candles, opts = {}) {
        this.drawings.push({
            type: 'candle',
            open: candles.open instanceof Float64Array ? Array.from(candles.open) : candles.open,
            high: candles.high instanceof Float64Array ? Array.from(candles.high) : candles.high,
            low: candles.low instanceof Float64Array ? Array.from(candles.low) : candles.low,
            close: candles.close instanceof Float64Array ? Array.from(candles.close) : candles.close,
            colorUp: opts.colorUp || '#26a69a',
            colorDown: opts.colorDown || '#ef5350',
            borderUp: opts.borderUp || opts.colorUp || '#26a69a',
            borderDown: opts.borderDown || opts.colorDown || '#ef5350',
            wickUp: opts.wickUp || opts.colorUp || '#26a69a',
            wickDown: opts.wickDown || opts.colorDown || '#ef5350',
            title: opts.title || '',
        });
    }
    arrow(series, opts = {}) {
        this.drawings.push({
            type: 'arrow',
            data: series instanceof Float64Array ? Array.from(series) : series,
            direction: opts.direction || 'up',
            color: opts.color || (opts.direction === 'down' ? '#ef5350' : '#26a69a'),
            size: opts.size || 1,
            text: opts.text || '',
        });
    }
    fill(series1, series2, opts = {}) {
        this.drawings.push({
            type: 'fill',
            data1: series1 instanceof Float64Array ? Array.from(series1) : series1,
            data2: typeof series2 === 'number' ? series2 : (series2 instanceof Float64Array ? Array.from(series2) : series2),
            color: opts.color || 'rgba(41, 98, 255, 0.1)',
            opacity: opts.opacity !== undefined ? opts.opacity : 0.1,
            title: opts.title || '',
        });
    }
    bgcolor(colors) {
        this.drawings.push({ type: 'bgcolor', data: colors });
    }
    histogram(values, opts = {}) {
        this.drawings.push({
            type: 'histogram',
            data: values instanceof Float64Array ? Array.from(values) : values,
            color: opts.color || '#26a69a',
            colorUp: opts.colorUp || opts.color || '#26a69a',
            colorDown: opts.colorDown || '#ef5350',
            width: opts.width || 0,
            title: opts.title || '',
        });
    }
}
        `;
    }

    /**
     * Handle messages from the worker
     */
    _handleMessage(data) {
        const { id, type } = data;

        if (type === 'result' || type === 'error' || type === 'pong') {
            const cb = this._pendingCallbacks.get(id);
            if (cb) {
                this._pendingCallbacks.delete(id);
                clearTimeout(cb.timer);

                if (type === 'error') {
                    cb.reject(new Error(data.error.message));
                } else {
                    cb.resolve(data);
                }
            }
        }
    }

    /**
     * Send a message to the worker and wait for response
     */
    _postMessage(message) {
        return new Promise((resolve, reject) => {
            const id = ++this._messageId;
            message.id = id;

            const timer = setTimeout(() => {
                this._pendingCallbacks.delete(id);
                reject(new Error(`Execution timeout (${EXECUTION_TIMEOUT}ms)`));
                // Terminate and recreate worker on timeout
                this._restartWorker();
            }, EXECUTION_TIMEOUT);

            this._pendingCallbacks.set(id, { resolve, reject, timer });
            this._worker.postMessage(message);
        });
    }

    /**
     * Restart worker after timeout or error
     */
    async _restartWorker() {
        this.destroy();
        await this.init();
    }

    /**
     * Execute an indicator script
     * @param {string} code - User's indicator code
     * @param {Object} input - OHLCV data { open, high, low, close, volume }
     * @param {Object} options - User-configured options
     * @returns {Promise<Object>} Execution result
     */
    async execute(code, input, options = {}) {
        // Step 1: Static validation
        const validation = validateCode(code);
        if (!validation.valid) {
            return {
                success: false,
                errors: validation.errors,
                warnings: validation.warnings,
                result: null,
                elapsed: 0,
            };
        }

        // Step 2: Check cache
        const dataLength = input.close ? input.close.length : 0;
        const cached = this._cache.get(code, options, input);
        if (cached) {
            return {
                success: true,
                errors: [],
                warnings: validation.warnings,
                result: cached,
                elapsed: 0,
                cached: true,
            };
        }

        // Step 3: Ensure worker is ready
        if (!this._worker || !this._ready) {
            await this.init();
        }

        // Step 4: Prepare input data (convert to transferable arrays)
        const serializedInput = {};
        for (const key of Object.keys(input)) {
            const val = input[key];
            if (val instanceof Float64Array) {
                serializedInput[key] = Array.from(val);
            } else if (Array.isArray(val)) {
                serializedInput[key] = val;
            } else {
                serializedInput[key] = val;
            }
        }

        // Step 5: Execute in worker
        try {
            const response = await this._postMessage({
                type: 'execute',
                code,
                input: serializedInput,
                options,
            });

            // Cache the result
            this._cache.set(code, options, input, response.result);

            return {
                success: true,
                errors: [],
                warnings: validation.warnings,
                result: response.result,
                elapsed: response.elapsed || 0,
                cached: false,
            };
        } catch (err) {
            return {
                success: false,
                errors: [{ type: 'runtime', message: err.message }],
                warnings: validation.warnings,
                result: null,
                elapsed: 0,
            };
        }
    }

    /**
     * Validate code without executing
     */
    validate(code) {
        return validateCode(code);
    }

    /**
     * Clear computation cache
     */
    clearCache() {
        this._cache.clear();
    }

    /**
     * Check if worker is alive
     */
    async ping() {
        if (!this._worker) return false;
        try {
            const response = await this._postMessage({ type: 'ping' });
            return response.type === 'pong';
        } catch {
            return false;
        }
    }

    /**
     * Destroy the worker and clean up
     */
    destroy() {
        if (this._worker) {
            this._worker.terminate();
            this._worker = null;
        }
        if (this._workerBlobUrl) {
            URL.revokeObjectURL(this._workerBlobUrl);
            this._workerBlobUrl = null;
        }
        this._ready = false;
        this._pendingCallbacks.clear();
        this._cache.clear();
    }
}

// Singleton instance
let _instance = null;

export function getExecutor() {
    if (!_instance) {
        _instance = new CandleScriptExecutor();
    }
    return _instance;
}

export default CandleScriptExecutor;
